import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, FileSearch } from 'lucide-react';
import { useDecideOcrManualReview, useOcrManualReview } from '../hooks/useAdminOps';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../components/ui';
import type { OcrManualReviewItem } from '../api/adminOps';
import { holdPresentation, queuedAt } from './documentReview.logic';

// Matches the decision endpoint's schema (note: z.string().max(500)).
const NOTE_MAX_LENGTH = 500;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-text uppercase tracking-wide">{label}</p>
      <div className="text-sm text-white mt-0.5 break-words">{children}</div>
    </div>
  );
}

export default function DocumentReview() {
  const [selected, setSelected] = useState<OcrManualReviewItem | null>(null);
  const [note, setNote] = useState('');

  const queue = useOcrManualReview();
  const decide = useDecideOcrManualReview();

  // Each document starts with a clean note; a stale note must never carry
  // over into the next decision's audit record.
  useEffect(() => {
    setNote('');
  }, [selected?.document_id]);

  const hold = selected === null ? null : holdPresentation(selected);
  const trimmedNote = note.trim();
  const canApprove = selected !== null && !decide.isPending;
  // Rejections notify the donor, so the reviewer must say why.
  const canReject = selected !== null && !decide.isPending && trimmedNote !== '';

  const submitDecision = (action: 'approve' | 'reject') => {
    if (selected === null || decide.isPending) return;
    if (action === 'reject' && trimmedNote === '') return;
    decide.mutate(
      {
        documentId: selected.document_id,
        action,
        note: trimmedNote === '' ? undefined : trimmedNote,
        expectedStatus: selected.processing_status ?? 'manual_review',
      },
      { onSuccess: () => setSelected(null) }
    );
  };

  // A/R shortcuts while the drawer is open, unless the reviewer is typing.
  useEffect(() => {
    if (selected === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'a') submitDecision('approve');
      if (key === 'r') submitDecision('reject');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  const columns: QueueColumn<OcrManualReviewItem>[] = [
    {
      key: 'kind',
      header: 'Hold',
      render: row => {
        const rowHold = holdPresentation(row);
        return <StatusChip label={rowHold.label} tone={rowHold.tone} />;
      },
    },
    {
      key: 'document',
      header: 'Document',
      render: row => (
        <div>
          <p>{row.file_name ?? row.document_id}</p>
          <p className="text-xs text-gray-text">{row.document_type}</p>
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: row => <span className="font-mono text-xs">{row.user_id}</span>,
    },
    {
      key: 'donation',
      header: 'Donation',
      render: row =>
        row.donation_id ? (
          <span className="font-mono text-xs">{row.donation_id}</span>
        ) : (
          <span className="text-gray-text">—</span>
        ),
    },
    {
      key: 'error',
      header: 'Processing Error',
      render: row =>
        row.processing_error ? (
          <StatusChip label={row.processing_error} tone="crit" />
        ) : (
          <span className="text-gray-text">—</span>
        ),
    },
    {
      key: 'created',
      header: 'Queued',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(queuedAt(row)), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
          <FileSearch className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Document Review</h1>
          <p className="text-sm text-gray-text">
            OCR quarantines and post-OCR holds (fraud, redaction or bank-statement mismatch);
            decisions notify the donor
          </p>
        </div>
      </div>

      {/* Queue */}
      {queue.isError ? (
        <div className="glass-card p-6">
          <div className="flex items-center space-x-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load review queue: {queue.error.message}</span>
          </div>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.document_id}
          isLoading={queue.isPending}
          onRowClick={row => setSelected(row)}
          emptyTitle="No documents awaiting review"
          emptyHint="Documents land here when OCR processing needs a human decision."
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      {/* Detail drawer */}
      <DetailDrawer
        open={selected !== null}
        title={selected?.file_name ?? 'Document review'}
        onClose={() => setSelected(null)}
        footer={
          selected !== null &&
          hold !== null && (
            <div className="space-y-3">
              <div className="text-xs text-gray-text space-y-1">
                <p>{hold.approveEffect}</p>
                <p>{hold.rejectEffect}</p>
              </div>
              <div>
                <label className="label" htmlFor="document-review-note">
                  Note{' '}
                  {trimmedNote === '' && (
                    <span className="text-gray-text">(required to reject)</span>
                  )}
                </label>
                <textarea
                  id="document-review-note"
                  className="input min-h-[80px]"
                  maxLength={NOTE_MAX_LENGTH}
                  placeholder="Why is this document being approved or rejected?"
                  value={note}
                  onChange={event => setNote(event.target.value)}
                />
                <p className="text-xs text-gray-text mt-1 text-right">
                  {note.length}/{NOTE_MAX_LENGTH}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => submitDecision('approve')}
                  disabled={!canApprove}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {decide.isPending ? 'Submitting…' : hold.approveLabel}
                </button>
                <button
                  onClick={() => submitDecision('reject')}
                  disabled={!canReject}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {decide.isPending ? 'Submitting…' : 'Reject'}
                </button>
              </div>
              <p className="text-xs text-gray-text text-center">
                Shortcuts: <kbd className="font-mono">A</kbd> approve ·{' '}
                <kbd className="font-mono">R</kbd> reject (note required)
              </p>
            </div>
          )
        }
      >
        {selected !== null && hold !== null && (
          <>
            <Field label="Hold">
              <StatusChip label={hold.label} tone={hold.tone} />
              <p className="text-xs text-gray-text mt-1">{hold.description}</p>
            </Field>
            <Field label="Document ID">
              <span className="font-mono text-xs">{selected.document_id}</span>
            </Field>
            <Field label="File name">
              {selected.file_name ?? <span className="text-gray-text">—</span>}
            </Field>
            <Field label="Document type">{selected.document_type}</Field>
            <Field label="User">
              <span className="font-mono text-xs">{selected.user_id}</span>
            </Field>
            <Field label="Donation">
              {selected.donation_id ? (
                <span className="font-mono text-xs">{selected.donation_id}</span>
              ) : (
                <span className="text-gray-text">Not linked to a donation</span>
              )}
            </Field>
            <Field label="Processing error">
              {selected.processing_error ? (
                <span className="text-red-400">{selected.processing_error}</span>
              ) : (
                <span className="text-gray-text">—</span>
              )}
            </Field>
            <Field label="Queued">
              {formatDistanceToNow(new Date(queuedAt(selected)), { addSuffix: true })}
            </Field>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
